/**
 * legal.js — Habeas Data: aceptación de Términos y Tratamiento de Datos.
 * Bloquea el acceso hasta aceptar los documentos obligatorios vigentes
 * (Ley 1581/2012). Registra la aceptación con IP/user-agent en el backend.
 */
const Legal = (() => {

  /**
   * gate() — Promise que resuelve cuando todos los documentos OBLIGATORIOS
   * vigentes han sido aceptados. Si hay pendientes, muestra un overlay
   * bloqueante. Resuelve `true` si se puede continuar, `false` si el usuario
   * cierra sesión sin aceptar.
   */
  async function gate() {
    let data;
    try {
      data = await API.get('/legal/pending');
    } catch {
      return true; // si el endpoint falla, no bloqueamos el portal
    }
    const pending = data?.pending || [];
    if (pending.length === 0) return true;

    return new Promise((resolve) => {
      _renderOverlay(pending, resolve);
    });
  }

  function _renderOverlay(pending, resolve) {
    document.getElementById('legal-overlay')?.remove();
    const ov = document.createElement('div');
    ov.id = 'legal-overlay';
    ov.className = 'legal-overlay';
    ov.innerHTML = `
      <div class="legal-modal">
        <div class="legal-head">
          <i class="bi bi-shield-check"></i>
          <div>
            <h2>Protección de Datos Personales</h2>
            <p>Antes de continuar, revisa y acepta los siguientes documentos
               (Ley 1581 de 2012).</p>
          </div>
        </div>
        <div class="legal-docs" id="legal-docs">
          ${pending.map((d, i) => _docBlock(d, i)).join('')}
        </div>
        <div class="legal-foot">
          <button class="btn btn-ghost" id="legal-logout">Cerrar sesión</button>
          <button class="btn btn-primary" id="legal-accept" disabled>
            <i class="bi bi-check2-circle"></i> Aceptar y continuar
          </button>
        </div>
      </div>`;
    document.body.appendChild(ov);

    const checks = pending.map((_, i) => document.getElementById(`legal-chk-${i}`));
    const acceptBtn = document.getElementById('legal-accept');

    function refresh() {
      // Habilita solo si TODOS los obligatorios están marcados
      const ok = pending.every((d, i) => !d.is_mandatory || checks[i].checked);
      acceptBtn.disabled = !ok;
    }
    checks.forEach(c => c.addEventListener('change', refresh));
    refresh();

    document.getElementById('legal-logout').addEventListener('click', () => {
      ov.remove();
      resolve(false);
      if (typeof Auth !== 'undefined') Auth.logout();
    });

    acceptBtn.addEventListener('click', async () => {
      acceptBtn.disabled = true;
      const items = pending.map((d, i) => ({
        doc_key: d.doc_key,
        accepted: d.is_mandatory ? true : checks[i].checked,
      }));
      try {
        await API.post('/legal/accept', { items });
        ov.classList.add('legal-closing');
        setTimeout(() => ov.remove(), 250);
        resolve(true);
      } catch (err) {
        Utils.toast(err.message || 'No se pudo registrar la aceptación', 'error');
        acceptBtn.disabled = false;
      }
    });
  }

  function _docBlock(d, i) {
    const tag = d.is_mandatory
      ? '<span class="legal-tag legal-tag-req">Obligatorio</span>'
      : '<span class="legal-tag legal-tag-opt">Voluntario</span>';
    return `
      <div class="legal-doc">
        <div class="legal-doc-title">${Utils.escapeHtml(d.title)} ${tag}
          <span class="legal-ver">v${d.version || '1.0'}</span>
        </div>
        <div class="legal-doc-body">${d.content || ''}</div>
        <label class="legal-chk-row">
          <input type="checkbox" id="legal-chk-${i}" />
          <span>${d.is_mandatory
            ? 'He leído y acepto este documento.'
            : 'Autorizo de forma voluntaria (puedes continuar sin marcar esta casilla).'}</span>
        </label>
      </div>`;
  }

  /** Visor de solo lectura — para enlaces del login y del perfil. */
  async function viewDocument(docKey) {
    try {
      const d = await API.get(`/legal/public/${docKey}`);
      Utils.modal({
        title: `<i class="bi bi-file-earmark-text"></i> ${Utils.escapeHtml(d.title)}`,
        size: '680px',
        body: `<div class="legal-doc-body legal-readonly">${d.content || ''}</div>
               <p class="legal-ver-note">Versión ${d.version || '1.0'}</p>`,
        footer: `<button class="btn btn-primary" onclick="Utils.closeModal()">Cerrar</button>`,
      });
    } catch {
      Utils.toast('No se pudo cargar el documento', 'error');
    }
  }

  return { gate, viewDocument };
})();
window.Legal = Legal;
